/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { checkboxSize, labelPlacement } from '../../registry/constants';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import { baseFieldProps, fontStyleProps } from '../TextField/BaseField';
import { defaultValue } from './Checkbox';

const CheckboxManager: ComponentManagerFn<'Checkbox'> = (
  platform: Platform,
  type: 'Checkbox',
): ComponentManager<'Checkbox'> => {
  return {
    ...baseComponentManager<'Checkbox'>(platform, 'Checkbox'),
    type: 'Checkbox',
    isChildAllowed: (page, item, childType) =>
      (
        ['TriggerOnChange', 'TriggerAfterServerValidate'] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseFieldProps,
      ...fontStyleProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: () => {
          return ['YN', 'Boolean', 'TF'];
        },
        helpText: 'Only attributes with type YN, TF and Boolean are allowed',
      },
      checkedValue: {
        type: 'string',
        label: 'Checked Value',
        maxLength: 1,
      },
      unCheckedValue: {
        type: 'string',
        label: 'Unchecked Value',
        maxLength: 1,
      },
      label: {
        label: 'Label',
        type: 'expression',
        returnType: async () => 'string',
      },
      boxLabel: {
        label: 'Box Label',
        type: 'expression',
        returnType: async () => 'string',
      },
      boxLabelPlacement: {
        label: 'Box Label Placement',
        type: 'select',
        options: labelPlacement,
        helpText: 'Specify the placement for the label to display.',
      },
      size: {
        label: 'Size',
        type: 'select',
        options: checkboxSize,
        required: true,
      },
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
        helpText:
          'Specify an expression which evaluate to true to disable this checkbox',
      },
      displayAsSwitch: {
        label: 'Display As Switch',
        type: 'boolean',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
    },
    isPropertyVisible(prop, itemData, { appUid, pageId, itemId }): boolean {
      switch (prop) {
        case 'required':
        case 'colorExpression':
        case 'textFontSize':
        case 'textBold':
        case 'textItalic':
          return false;
        case 'checkedValue':
        case 'unCheckedValue': {
          const store = platform.getParentStore(appUid, pageId, itemId);
          const attr = store?.getAttribute(itemData.viewAttribute);
          return attr?.attributeType !== 'Boolean';
        }
        case 'label':
        case 'flex':
        case 'helperText': {
          const inForm = platform.findParentOfType(
            appUid,
            pageId,
            itemId,
            'Form',
          );
          if (inForm) {
            return true;
          }
          const parent = platform.findParentOfType(
            appUid,
            pageId,
            itemId,
            'Toolbar',
          );
          return !parent;
        }
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    name: 'Checkbox',
    devStyles: { display: 'inline-flex' },
    category: 'Forms',
    icon: { iconName: 'check-square' },
    replaceableWith: ['RadioBox', 'TextField'],
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    description: `**Overview**<br/>
The Checkbox is a form component used for binary choices (True/False, Yes/No). It allows users to toggle an option on or off.
Depending on the context, it can appear as a standard square box or a modern "Toggle Switch," making it versatile for both simple forms and settings pages.

**1. Data Binding & Values**<br/>
To store the user's choice, this component binds to a boolean-compatible attribute in your Data Store.
* **View Attribute:** Select the Data Store field. (Supported Types: Boolean, YN, TF).
* **Custom Value Mapping (Legacy Support):** If your database uses specific characters instead of standard Booleans (e.g., 'Y' for true and 'N' for false), you can map them explicitly.
    * **Checked Value:** The string to save when checked (e.g., 'Y').
    * **Unchecked Value:** The string to save when unchecked (e.g., 'N').
    * **Note:** These properties are hidden if you bind to a standard Boolean attribute.

**2. Visual Configuration**<br/>
* **Box Label:** The text displayed immediately next to the checkbox itself (e.g., "I agree to terms").
* **Label:** The field label displayed above or beside the component layout (standard Form Field label).
* **Display As Switch:**
    * **Enabled:** Renders as a slider toggle (switch). Best for "Settings" (e.g., "Enable Notifications").
    * **Disabled:** Renders as a standard square checkbox. Best for forms or lists.
* **Size:** Controls the visual size of the box (Small, Medium, Large).
* **Box Label Placement:** Controls where the Box Label appears relative to the checkbox (Start, End, Top, Bottom).

**3. Interaction & Logic**<br/>
* **Disable Condition:** A boolean expression (e.g., \`$.user.role !== 'Admin'\`). If true, the checkbox is rendered in a disabled state (greyed out) and cannot be toggled.
* **Server Validate On Change:** Triggers a backend validation workflow immediately when the value changes.
* **TriggerOnChange:** You can add this child component to execute client-side logic immediately after the user toggles the box.`,
    helpRenders: [
      {
        title: 'Checkbox',
        description: 'With label & help text',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        visibleProps: ['label', 'helperText'],
        item: {
          itemId: 'tf',
          props: {
            viewAttribute: 'userName',
            label: { type: 'text', value: 'Enable Something' },
            helperText: 'Help text for this check box...',
            flex: { all: 12 },
          },
          children: [],
          type: 'Checkbox',
          parent: 'form',
        },
      },
      {
        title: 'Box Label',
        page: SamplePage,
        exampleId: 'e2',
        visibleProps: ['boxLabel'],
        item: {
          itemId: 'tf1',
          props: {
            viewAttribute: 'userName',
            boxLabel: { type: 'text', value: 'Enable Something' },
            flex: { all: 12 },
          },
          children: [],
          type: 'Checkbox',
          parent: 'form',
        },
      },
      {
        title: 'With Disabled Condition',
        page: SamplePage,
        exampleId: 'e3',
        visibleProps: ['disableCondition'],
        item: {
          itemId: 'tf1',
          props: {
            viewAttribute: 'userName',
            boxLabel: { type: 'text', value: 'Enable Something' },
            flex: { all: 12 },
            disableCondition: '1===1',
          },
          children: [],
          type: 'Checkbox',
          parent: 'form',
        },
      },
      {
        title: 'Switch',
        page: SamplePage,
        exampleId: 'e4',
        visibleProps: ['displayAsSwitch'],
        item: {
          itemId: 'tf1',
          props: {
            viewAttribute: 'userName',
            boxLabel: { type: 'text', value: 'Enable Something' },
            flex: { all: 12 },
            displayAsSwitch: true,
          },
          children: [],
          type: 'Checkbox',
          parent: 'form',
        },
      },
    ],
  };
};

export default CheckboxManager;
